import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, CircularProgress, CssBaseline, Grid, Typography } from '@mui/material';
import { useQuery } from '@apollo/client';

import i18next from 'i18next';

import { gql } from '__generated__/gql';

/**
 * Fetches the platform login URL from backend.
 * Platform handles both customer and backoffice logins.
 */
export const PLATFORM_LOGIN_URL_QUERY = gql(`
  query PlatformLoginUrlQuery {
    platformLoginUrl
  }
`);

/**
 * Login mutation for direct authentication (kept for potential future use).
 */
export const LOGIN_MUTATION = gql(`
  mutation LoginMutation($email: String!, $password: String!) {
    login(email: $email, password: $password) {
      success
      user {
        ...UserFull
      }
    }
  }
`);

/**
 * Sends password reset email to user.
 */
export const FORGOTPASSWORD_MUTATION = gql(`
  mutation ForgotUserPasswordMutation($email: String!) {
    forgotUserPassword(email: $email)
  }
`);

/**
 * Logs out the current user and invalidates session.
 */
export const LOGOUT_MUTATION = gql(`
  mutation LogoutMutation {
    logout
  }
`);

/**
 * Full user query including spaces and hotels.
 * Used for non-Seminargo users who need space/hotel data.
 */
export const ME_QUERY = gql(`
  query MeQuery {
    me {
      ...UserFull
    }
    listSpaces {
      ...SpaceForDropdown
    }
    listHotels {
      hotels {
        ...HotelForDropdown
      }
    }
  }
`);

/**
 * Minimal user query without spaces/hotels for Seminargo users.
 * Performance optimization since Seminargo users don't need space/hotel data on initial load.
 */
export const ME_QUERY_MINIMAL = gql(`
  query MeQueryMinimal {
    me {
      ...UserFull
    }
  }
`);

/**
 * Separate query for spaces/hotels, used after ME_QUERY_MINIMAL.
 * Loads data only for non-Seminargo users after determining user type.
 */
export const SPACES_AND_HOTELS_QUERY = gql(`
  query SpacesAndHotelsQuery {
    listSpaces {
      ...SpaceForDropdown
    }
    listHotels {
      hotels {
        ...HotelForDropdown
      }
    }
  }
`);

/**
 * Checks if maintenance mode is active.
 * Used to redirect users during maintenance windows.
 */
export const MAINTENANCE_MODE_QUERY = gql(`
  query MaintenanceModeQuery {
    maintenanceMode
  }
`);

interface LoginRedirectProps {
  passwordResetCode?: string | null;
}

/**
 * Redirects to platform login page.
 * The platform handles both customer and backoffice (hotel) logins.
 * After successful backoffice login, the platform redirects back to this app.
 */
export default function LoginRedirect(props: LoginRedirectProps) {
  const location = useLocation();

  // Query platform login URL from backend
  const { data, loading, error } = useQuery(PLATFORM_LOGIN_URL_QUERY, {
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (data?.platformLoginUrl) {
      let targetUrl = data.platformLoginUrl;

      // Handle password reset redirect
      if (props.passwordResetCode) {
        // Redirect to platform's password reset page
        targetUrl = targetUrl.replace('/login', `/password/reset/${props.passwordResetCode}`);
      } else {
        // Pass the original "from" parameter so platform can redirect back after login
        const params = new URLSearchParams(location.search);
        const from = params.get('from');
        if (from) {
          const url = new URL(targetUrl);
          url.searchParams.set('redirect', from);
          targetUrl = url.toString();
        }
      }

      window.location.href = targetUrl;
    }
  }, [data, props.passwordResetCode, location.search]);

  // Show loading state while fetching URL or redirecting
  return (
    <Grid container component="main" sx={{ height: '100vh' }}>
      <CssBaseline />
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: t => t.palette.grey[100],
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body1" color="textSecondary">
          {i18next.t('loading')}
        </Typography>
      </Grid>
    </Grid>
  );
}
